var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { FormGroupVariant, FormLabelPlacement, FormSectionVariant, } from '../../../interfaces/createApplication';
import DynamicFormField from './DynamicFormField';
import { dynamicGroupSurfaceSx, dynamicGroupTitleSx, dynamicSectionSurfaceSx, SECTION_HEADER_ROW_SX, SECTION_TITLE_SX, } from './dynamic_form_styles';
var GROUP_HEADER_ROW_SX = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    mb: 1.25,
};
var DEFAULT_GRID_SIZE = { xs: 12, sm: 6, md: 4, lg: 3 };
var FULL_WIDTH_GRID_SIZE = { xs: 12, sm: 12, md: 12, lg: 12 };
var EMPTY_OPTIONS = [];
var FULL_ROW_COLUMNS = 12;
var collectDependencyNames = function (section) {
    var names = new Set();
    section.groups.forEach(function (group) {
        var _a;
        (_a = group.dependsOn) === null || _a === void 0 ? void 0 : _a.forEach(function (name) { return names.add(name); });
        group.fields.forEach(function (field) {
            var _a;
            (_a = field.dependsOn) === null || _a === void 0 ? void 0 : _a.forEach(function (name) { return names.add(name); });
        });
    });
    return Array.from(names).sort();
};
var isStillOcrValue = function (ocrValues, fieldName, value) {
    var _a;
    return Boolean(ocrValues) &&
        Object.prototype.hasOwnProperty.call(ocrValues, fieldName) &&
        String(value !== null && value !== void 0 ? value : '').trim() === String((_a = ocrValues === null || ocrValues === void 0 ? void 0 : ocrValues[fieldName]) !== null && _a !== void 0 ? _a : '').trim();
};
var hasLabelAbove = function (field) {
    return field.labelPlacement === FormLabelPlacement.Above;
};
var needsLabelSpacer = function (fields) {
    return fields.some(function (_a) {
        var _b;
        var field = _a.field, gridSize = _a.gridSize;
        return hasLabelAbove(field) && ((_b = gridSize.md) !== null && _b !== void 0 ? _b : FULL_ROW_COLUMNS) < FULL_ROW_COLUMNS;
    }) && fields.some(function (_a) {
        var field = _a.field;
        return !hasLabelAbove(field);
    });
};
var DynamicFormSection = function (_a) {
    var _b;
    var section = _a.section, values = _a.values, errors = _a.errors, onChange = _a.onChange, onBlur = _a.onBlur, onFileSelect = _a.onFileSelect, onAction = _a.onAction, onAutofill = _a.onAutofill, headerAbove = _a.headerAbove, headerContent = _a.headerContent, headerAction = _a.headerAction, groupActions = _a.groupActions, lockedFieldNames = _a.lockedFieldNames, ocrFilledValues = _a.ocrFilledValues, searchOptions = _a.searchOptions, onSearchInput = _a.onSearchInput, onSearchSelect = _a.onSearchSelect;
    var valuesRef = React.useRef(values);
    valuesRef.current = values;
    var lockedFields = React.useMemo(function () { return new Set(lockedFieldNames !== null && lockedFieldNames !== void 0 ? lockedFieldNames : []); }, [lockedFieldNames]);
    var dependencyNames = React.useMemo(function () { return collectDependencyNames(section); }, [section]);
    var dependencyKey = React.useMemo(function () { return dependencyNames.map(function (name) { var _a; return "".concat(name, "=").concat(String((_a = values[name]) !== null && _a !== void 0 ? _a : '')); }).join('|'); }, [dependencyNames, values]);
    var resolvedGroups = React.useMemo(function () {
        return section.groups.map(function (group) {
            var _a, _b;
            var variant = (_a = group.variant) !== null && _a !== void 0 ? _a : FormGroupVariant.Plain;
            var fields = group.fields.map(function (originalField) {
                var _a, _b;
                return ({
                    field: originalField.getLabel
                        ? __assign(__assign({}, originalField), { label: originalField.getLabel(valuesRef.current) }) : originalField,
                    options: (originalField.searchKey && (searchOptions === null || searchOptions === void 0 ? void 0 : searchOptions[originalField.name])) ||
                        (originalField.getOptions
                            ? originalField.getOptions(valuesRef.current)
                            : (_a = originalField.options) !== null && _a !== void 0 ? _a : EMPTY_OPTIONS),
                    isVisible: originalField.isVisible
                        ? originalField.isVisible(valuesRef.current)
                        : true,
                    isLocked: lockedFields.has(originalField.name),
                    isDisabled: lockedFields.has(originalField.name) ||
                        (originalField.isDisabled
                            ? originalField.isDisabled(valuesRef.current)
                            : false),
                    gridSize: __assign(__assign({}, DEFAULT_GRID_SIZE), ((_b = originalField.gridSize) !== null && _b !== void 0 ? _b : {})),
                });
            });
            return {
                group: group,
                title: group.getTitle ? group.getTitle(valuesRef.current) : group.title,
                isVisible: group.isVisible ? group.isVisible(valuesRef.current) : true,
                fields: fields,
                gridSize: __assign(__assign({}, FULL_WIDTH_GRID_SIZE), ((_b = group.gridSize) !== null && _b !== void 0 ? _b : {})),
                isCard: variant === FormGroupVariant.Card,
                isHighlighted: variant === FormGroupVariant.Highlighted,
                hasLabelSpacer: needsLabelSpacer(fields),
                action: groupActions === null || groupActions === void 0 ? void 0 : groupActions[group.id],
            };
        });
    }, [section, dependencyKey, lockedFields, searchOptions, groupActions]);
    if (section.isVisible && !section.isVisible(values)) {
        return null;
    }
    var isFilled = ((_b = section.variant) !== null && _b !== void 0 ? _b : FormSectionVariant.Filled) === FormSectionVariant.Filled;
    return (React.createElement(Box, null,
        headerAbove,
        React.createElement(Box, { component: "section", "aria-labelledby": section.title ? "".concat(section.id, "-title") : undefined, sx: dynamicSectionSurfaceSx(isFilled) },
            (section.title || headerAction) && (React.createElement(Box, { sx: SECTION_HEADER_ROW_SX },
                section.title && (React.createElement(Typography, { id: "".concat(section.id, "-title"), component: "h3", sx: SECTION_TITLE_SX }, section.title)),
                headerAction)),
            headerContent,
            React.createElement(Grid, { container: true, spacing: 2.5, alignItems: "stretch" }, resolvedGroups
                .filter(function (resolvedGroup) { return resolvedGroup.isVisible; })
                .map(function (_a) {
                var group = _a.group, groupTitle = _a.title, fields = _a.fields, gridSize = _a.gridSize, isCard = _a.isCard, isHighlighted = _a.isHighlighted, hasLabelSpacer = _a.hasLabelSpacer, groupAction = _a.action;
                return (React.createElement(Grid, { item: true, key: group.id, xs: gridSize.xs, sm: gridSize.sm, md: gridSize.md, lg: gridSize.lg },
                    React.createElement(Box, { sx: dynamicGroupSurfaceSx(isCard, isHighlighted) },
                        (groupTitle || groupAction) && (React.createElement(Box, { sx: GROUP_HEADER_ROW_SX },
                            groupTitle && (React.createElement(Typography, { component: "h4", sx: __assign(__assign({}, dynamicGroupTitleSx(isCard)), { mb: 0 }) }, groupTitle)),
                            groupAction)),
                        React.createElement(Grid, { container: true, spacing: 2, alignItems: "flex-start" }, fields
                            .filter(function (resolvedField) { return resolvedField.isVisible; })
                            .map(function (_a) {
                            var _b, _c;
                            var field = _a.field, options = _a.options, isDisabled = _a.isDisabled, isLocked = _a.isLocked, fieldGrid = _a.gridSize;
                            return (React.createElement(Grid, { item: true, key: field.name, xs: fieldGrid.xs, sm: fieldGrid.sm, md: fieldGrid.md, lg: fieldGrid.lg },
                                React.createElement(DynamicFormField, { field: field, value: (_b = values[field.name]) !== null && _b !== void 0 ? _b : '', error: (_c = errors[field.name]) !== null && _c !== void 0 ? _c : '', options: options, isDisabled: isDisabled, isLocked: isLocked, onChange: onChange, onBlur: onBlur, onFileSelect: onFileSelect, onAction: onAction, onAutofill: onAutofill, onSearchInput: onSearchInput, onSearchSelect: onSearchSelect, hasLabelSpacer: hasLabelSpacer, isOcrPrefilled: isStillOcrValue(ocrFilledValues, field.name, values[field.name]) })));
                        })))));
            })))));
};
export default React.memo(DynamicFormSection);
